import { HttpClient } from "@angular/common/http";
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  FormArray,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Modal } from "bootstrap";
import Cropper from "cropperjs";
import * as L from "leaflet";
import moment from "moment";

import Swal from "sweetalert2";
import { CategoryService } from "../../category/category.service";
import { CnpjService } from "../../../services/cnpj.service";
import { CompanyService } from "../company.service";
import { StorageService } from "../../../services/storage.service";
import { ImageService } from "../../../services/image.service";
import { UtilService } from "../../../services/util.service";
import { CommonModule } from "@angular/common";

@Component({
  selector: "app-company-form",
  templateUrl: "./company-form.component.html",
  styleUrls: ["./company-form.component.scss"],
  imports: [CommonModule, ReactiveFormsModule],
})
export class CompanyFormComponent implements OnInit, AfterViewInit {
  private map: L.Map | undefined;

  companyForm!: UntypedFormGroup;
  company: any;
  categories: any[] = [];
  cidade: any = "";
  customIcon: any;
  id!: string;

  @ViewChild("image", { static: false })
  imageElement!: ElementRef<HTMLImageElement>;
  @ViewChild("fileInput", { static: false })
  fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild("modalCropper") modalElement!: ElementRef; // Referência ao modal

  cropper!: Cropper;
  croppedImage!: string | null;
  modal!: Modal;

  private markers: L.Marker[] = []; // Array para armazenar os marcadores
  private pendingMapGeo: { lat: number; lng: number; accuracy: number } | null =
    null;

  constructor(
    @Inject(CategoryService) private categoryService: CategoryService,
    @Inject(StorageService) private storageService: StorageService,
    @Inject(ImageService) private imageService: ImageService,
    @Inject(UtilService) private utilService: UtilService,
    @Inject(CnpjService) private cnpjService: CnpjService,
    @Inject(CompanyService) private companyService: CompanyService,
    @Inject(HttpClient) private http: HttpClient,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  async ngOnInit() {
    this.listCategories();
    this.id = this.route.snapshot.paramMap.get("id") || "";
    this.customIcon = L.icon({
      iconUrl: "assets/img/gps.png",
      iconSize: [30, 39],
      iconAnchor: [16, 32],
      popupAnchor: [0, -32],
    });
    this.criarFormulario();
    if (this.id) {
      await this.carregarFormulario(this.id);
    }
  }

  async carregarFormulario(id: string) {
    const data = await this.companyService.getCompanyById(id);
    if (!data) {
      Swal.fire("Erro!", "Empresa não encontrada.", "error");
      this.router.navigate(["/empresas"]);
      return;
    }

    this.company = data;
    this.setTelefones(data.telefones);
    this.companyForm.patchValue({
      ...data,
      email: data.email ?? "",
      cashbackPercentual: data.cashbackPercentual ?? 5,
      limiteCompra: data.limiteCompra ?? 200,
      geolocalizacao: data.geolocalizacao ?? { lat: 0, lng: 0, accuracy: 0 },
      socials: {
        facebook: data.socials?.facebook ?? "",
        instagram: data.socials?.instagram ?? "",
        linkedin: data.socials?.linkedin ?? "",
        whatsapp: data.socials?.whatsapp ?? "",
      },
      status: data.status ?? 1,
    });

    const geo = this.companyForm.get("geolocalizacao")?.value;
    if (geo?.lat && geo?.lng) {
      this.applyMapGeo(geo);
    }
  }

  private applyMapGeo(geo: { lat: number; lng: number; accuracy: number }) {
    if (!this.map) {
      this.pendingMapGeo = geo;
      return;
    }
    const zoom = Number(geo.accuracy) || 13;
    this.map.setView([+geo.lat, +geo.lng], zoom);
    this.markers.forEach((marker) => this.map?.removeLayer(marker));
    this.markers = [];
    const marker = L.marker([+geo.lat, +geo.lng], {
      icon: this.customIcon,
    }).addTo(this.map);
    this.markers.push(marker);
  }

  private setTelefones(telefones?: string[]) {
    while (this.telefones.length) {
      this.telefones.removeAt(0);
    }
    const list = telefones?.length ? telefones : [""];
    list.forEach((telefone) => {
      this.telefones.push(
        this.formBuilder.control(telefone, [Validators.required])
      );
    });
  }

  criarFormulario(data?: any) {
    this.companyForm = this.formBuilder.group({
      id: [data?.id ?? null],
      companyId: [data?.companyId ?? ""],
      cnpj: [data?.cnpj ?? "", Validators.required],
      email: [data?.email ?? "", [Validators.required, Validators.email]],
      nomeFantasia: [data?.nomeFantasia ?? "", Validators.required],
      razaoSocial: [data?.razaoSocial ?? "", Validators.required],
      endereco: [data?.endereco ?? "", Validators.required],
      cep: [data?.cep ?? "", Validators.required],
      bairro: [data?.bairro ?? "", Validators.required],
      complemento: [data?.complemento ?? ""],
      numero: [data?.numero ?? "", Validators.required],
      uf: [data?.uf ?? "", Validators.required],
      municipio: [data?.municipio ?? "", Validators.required],
      geolocalizacao: this.formBuilder.group({
        lat: [data?.geolocalizacao?.lat ?? 0],
        lng: [data?.geolocalizacao?.lng ?? 0],
        accuracy: [data?.geolocalizacao?.accuracy ?? 0],
      }),
      categoria: [data?.categoria ?? "", Validators.required],
      cashbackPercentual: [
        data?.cashbackPercentual ?? 5,
        [Validators.required, Validators.min(0.1), Validators.max(100)],
      ],
      limiteCompra: [
        data?.limiteCompra ?? 200,
        [Validators.required, Validators.min(1)],
      ],
      socials: this.formBuilder.group({
        facebook: [data?.socials?.facebook ?? ""],
        instagram: [data?.socials?.instagram ?? ""],
        linkedin: [data?.socials?.linkedin ?? ""],
        whatsapp: [data?.socials?.whatsapp ?? ""],
      }),
      createdAt: [data?.createdAt ?? moment().toISOString()],
      foto: [data?.foto ?? ""],
      isOnline: [data?.isOnline ?? false],
      telefones: this.formBuilder.array(
        (data?.telefones && data.telefones.length > 0
          ? data.telefones
          : [""]
        ).map((telefone: string) =>
          this.formBuilder.control(telefone, [Validators.required])
        )
      ),
      status: [data?.status ?? 1],
    });
  }

  get telefones(): FormArray {
    return this.companyForm.get("telefones") as FormArray;
  }

  adicionarTelefone(): void {
    const telefoneControl = this.formBuilder.control("", [Validators.required]);
    this.telefones.push(telefoneControl);
  }

  removerTelefone(index: number): void {
    this.telefones.removeAt(index);
  }

  async consultarCNPJ() {
    const cnpj = this.companyForm.get("cnpj")?.value;

    // Em edição, não bloqueia o próprio CNPJ já cadastrado
    if (!this.id) {
      const cnpjExiste = await this.companyService.getCompanyByCNPJ(cnpj);
      if (cnpjExiste) {
        Swal.fire("ATENÇÃO", "CNPJ já cadastrado!", "warning");
        return;
      }
    }

    if (cnpj && !this.utilService.validarCNPJ(cnpj)) {
      Swal.fire("Erro!", "CNPJ inválido", "error");
    } else if (cnpj) {
      this.cnpjService.consultarCnpj(cnpj).subscribe((data) => {
        this.companyForm.patchValue({
          nomeFantasia: data?.fantasia,
          razaoSocial: data?.nome,
          endereco: data?.logradouro,
          cep: data?.cep.replace(/[^0-9]/g, ""),
          bairro: data?.bairro,
          complemento: data?.complemento,
          numero: data?.numero,
          uf: data?.uf,
          municipio: data?.municipio,
        });
        this.buscarCidade();
      });
    } else {
      Swal.fire("Erro!", "Ocorreu um erro ao consultar o CNPJ", "error");
    }
  }

  listCategories() {
    this.categoryService.getAll().subscribe((res) => {
      this.categories = res.sort((a, b) =>
        a.description.localeCompare(b.description)
      );
    });
  }

  async formSubmit() {
    if (this.companyForm.invalid) {
      Swal.fire(
        "Atenção",
        "Existem campos obrigatórios que não foram preenchidos.",
        "warning"
      );
      return;
    } else {
      try {
        const formValue = {
          ...this.companyForm.value,
          email: String(this.companyForm.value.email ?? "")
            .trim()
            .toLowerCase(),
          geolocalizacao: {
            ...this.companyForm.value.geolocalizacao,
            lat: Number(this.companyForm.value.geolocalizacao.lat) || 0,
            lng: Number(this.companyForm.value.geolocalizacao.lng) || 0,
            accuracy:
              Number(this.companyForm.value.geolocalizacao.accuracy) || 0,
          },
          cashbackPercentual: Number(
            this.companyForm.value.cashbackPercentual
          ),
          limiteCompra: Number(this.companyForm.value.limiteCompra),
          isOnline: Boolean(this.companyForm.value.isOnline),
          status: Number(this.companyForm.value.status) || 1,
        };

        if (this.id) {
          await this.companyService.update(this.id, formValue).then(() => {
            this.router.navigate(["/empresas"]);
          });
        } else {
          await this.companyService.create(formValue).then(() => {
            this.router.navigate(["/empresas"]);
          });
        }
      } catch (error) {
        Swal.fire(
          "Erro!",
          "Não foi possível salvar os seus dados. Tente novamente mais tarde.",
          "error"
        );
      }
    }
  }

  private initMap(): void {
    this.map = L.map("map").setView([-23.55052, -46.633308], 13);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map);

    this.map.on("zoomend", () => {
      const zoom = this.map?.getZoom();
      this.companyForm.get("geolocalizacao")?.patchValue({
        accuracy: Number(zoom),
      });
    });

    // Capturar o evento de clique no mapa
    this.map.on("click", (event: L.LeafletMouseEvent) => {
      const { lat, lng } = event.latlng;

      this.markers.forEach((marker) => {
        this.map?.removeLayer(marker); // Remove o marcador do mapa
      });
      this.markers = [];

      const marker = L.marker([+lat, +lng], { icon: this.customIcon }).addTo(
        this.map!
      );
      this.markers.push(marker);

      //Adicionar o valor no formulário
      this.companyForm.get("geolocalizacao")?.patchValue({
        lat: lat,
        lng: lng,
      });
    });
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.modal = new Modal(this.modalElement.nativeElement);
    if (this.pendingMapGeo) {
      this.applyMapGeo(this.pendingMapGeo);
      this.pendingMapGeo = null;
    }
  }

  buscarCidade(): void {
    const municipio = this.companyForm.get("municipio")?.value;
    const uf = this.companyForm.get("uf")?.value;
    const endereco = this.companyForm.get("endereco")?.value;
    const numero = this.companyForm.get("numero")?.value;

    const termo = `${endereco} ${numero}, ${municipio}-${uf}`;

    if (municipio.trim() === "") {
      Swal.fire("Erro!", "Por favor, digite o nome do município", "error");
      return;
    }

    const apiUrl = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
      termo
    )}&format=json&limit=1`;

    this.http.get<any[]>(apiUrl).subscribe({
      next: (response) => {
        if (response.length > 0) {
          const { lat, lon, display_name } = response[0];
          this.map?.setView([+lat, +lon], 13);
          this.map?.setZoom(17);

          //limpa os marcadores, antes de fazer uma nova marcação
          this.markers.forEach((marker) => {
            this.map?.removeLayer(marker); // Remove o marcador do mapa
          });
          this.markers = [];

          const marker = L.marker([+lat, +lon], {
            icon: this.customIcon,
          }).addTo(this.map!);
          this.markers.push(marker);
          const zoom = this.map?.getZoom();
          //Adicionar o valor no formulário
          this.companyForm.get("geolocalizacao")?.patchValue({
            lat: lat,
            lng: lon,
            accuracy: zoom,
          });
        } else {
          alert("Cidade não encontrada.");
          Swal.fire("", "Cidade não encontrada", "warning");
        }
      },
      error: (error) => {
        console.error("Erro ao buscar a localização:", error);
        Swal.fire(
          "Erro!",
          "Erro ao buscar a localização. Tente novamente mais tarde.",
          "error"
        );
      },
    });
  }

  openModal() {
    this.croppedImage = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = "";
    }
    this.modal.show();
  }

  closeModal() {
    if (this.modal) {
      this.modal.hide();
      if (this.cropper) {
        this.cropper.destroy();
      }

      if (this.imageElement) {
        this.imageElement.nativeElement.src = "";
      }

      this.croppedImage = null;
    }
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.imageService.resizeImage(file, (resizedImage: HTMLImageElement) => {
        this.imageElement.nativeElement.src = resizedImage.src;
        setTimeout(() => {
          if (this.cropper) {
            this.cropper.destroy();
          }
          this.cropper = new Cropper(this.imageElement.nativeElement, {
            aspectRatio: 1,
            viewMode: 1,
            autoCropArea: 0.8,
            responsive: true,
            scalable: true,
            zoomable: true,
            rotatable: true,
            checkOrientation: true,
            crop: () => {
              const canvas = this.cropper.getCroppedCanvas({
                width: 300,
                height: 300,
              });
              this.croppedImage = canvas.toDataURL("image/png");
            },
          });
        }, 100);
      });
    }
  }

  async uploadImage() {
    if (!this.croppedImage) return;
    const filePath = `profile/${new Date().getTime()}.png`;
    await this.storageService
      .uploadImage(filePath, this.croppedImage)
      .then((res) => {
        const fotoAntiga = this.companyForm.get("foto")?.value;
        this.storageService.deleteImage(fotoAntiga);
        this.companyForm.patchValue({
          foto: res,
        });
        //Atualiza o registro no firebase, apenas se for editar
        if (this.id) {
          this.companyService.updatePhoto(this.id, res);
        }
        this.modal.hide();
      });
  }
}
